import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SpiderwebLink } from "@/components/interlinking/SpiderwebLink";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GoneNotice from "@/components/seo/GoneNotice";
import { Button } from "@/components/ui/button";
import { 
  MapPin, Star, Phone, Globe, Mail, Clock, Award, ExternalLink, Crown, 
  Sparkles, CheckCircle2, TrendingUp, Heart, Users, Target, Trophy, Image as ImageIcon, Home
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useTracking } from "@/hooks/useTracking";
import { supabase } from "@/integrations/supabase/client";
import { UnirseATribuButton } from "@/components/shared/UnirseATribuButton";
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import { VotingSection } from "@/components/listing/VotingSection";
import { SocialShare } from "@/components/listing/SocialShare";
import { CommentsSection } from "@/components/listing/CommentsSection";
import { UnifiedMetrics } from "@/components/listing/UnifiedMetrics";
import { OnboardingGuide } from "@/components/listing/OnboardingGuide";
import { ProfileCompletionBar } from "@/components/listing/ProfileCompletionBar";
import { ValueActivator } from "@/components/listing/ValueActivator";
import { OpeningHours } from "@/components/listing/OpeningHours";
import { PricingInfo } from "@/components/listing/PricingInfo";
import { ClassSchedules } from "@/components/listing/ClassSchedules";
import { Testimonials } from "@/components/listing/Testimonials";
import { ListingCoupons } from "@/components/listing/ListingCoupons";
import { ListingEvents } from "@/components/listing/ListingEvents";
import { PremiumHero } from "@/components/listing/PremiumHero";
import { PremiumTrustSignals } from "@/components/listing/PremiumTrustSignals";
import { StickyPremiumCTA } from "@/components/listing/StickyPremiumCTA";
import { PremiumValueProposition } from "@/components/listing/PremiumValueProposition";
import { useABTest } from "@/hooks/useABTest";
import SEO from "@/components/SEO";

import { generateLocalBusinessSchema, generateBreadcrumbSchema } from "@/utils/seo";
import { SITE_URL } from "@/constants/seo";
import { generateListingMetaDescription } from "@/utils/metaDescriptions";
import { generateListingTitle } from "@/utils/seoTitles";
import { countryName, isHomeCountry, normalizeCountryCode } from "@/utils/countries";
import SimpleLocationMap from "@/components/SimpleLocationMap";
import { VoteButton } from "@/components/voting/VoteButton";
import { RelatedListingsSection } from "@/components/listing/RelatedListingsSection";
import { ProfessionalProfilesRow } from "@/components/listing/ProfessionalProfilesRow";
import { ManagementAccessCard } from "@/components/dashboard/ManagementAccessCard";
import LeadsSidebar from "@/components/listing/LeadsSidebar";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import { BrandedImage } from "@/components/images/BrandedImage";
import { getCategoryProgramType, getProgramName, getProgramLogoUrl, getProgramBranding } from "@/config/programBranding";
import { PublicStatsDisplay } from "@/components/shared/PublicStatsDisplay";

const defaultListingImage = "/default-listing-fallback.png";

const hasMarkdown = (s: string) => /(\n|^)#{1,6}\s|\*\*|\n-\s|\n\d+\.\s|`|_{1,3}[^_]+_{1,3}/.test(s);

const formatToMarkdown = (text: string) => {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  const sentences = clean.split(/(?<=\.)\s+/).filter(Boolean);
  const intro = sentences.slice(0, 3).join(' ');
  const services = sentences.slice(3, 8).map(s => `- ${s.trim()}`).join('\n');
  const reasons = sentences.slice(8, 12).map(s => `- **${s.trim()}**`).join('\n');
  const cta = '¿Listo para empezar? **Reserva tu primera visita hoy** y descubre por qué somos la mejor opción en tu zona.';

  return `## 🎯 Nuestra Valoración\n${intro}\n\n## Servicios y Beneficios\n${services}\n\n## ¿Por qué elegirnos?\n${reasons}\n\n## ¡Únete ahora!\n${cta}`;
};

const normalizeHeadings = (md: string) => {
  if (!md) return md as string;
  return md.replace(/(^|\n)##\s*Introducción Atractiva(\s*\n)/gi, '$1## 🎯 Nuestra Valoración$2');
};

const ListingDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [listing, setListing] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [cityActivityRankings, setCityActivityRankings] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const { trackContactClick, trackSocialShare } = useTracking();
  
  const { variant, trackConversion } = useABTest('listing', listing?.id || '');

  const displayData = {
    name: variant?.overrides?.title || listing?.name,
    short_description: variant?.overrides?.short_description || listing?.short_description,
    description: variant?.overrides?.description || listing?.description,
    cta_text: variant?.overrides?.cta_text || 'Contactar Ahora'
  };

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
      
      if (user && listing && user.id === listing.user_id) {
        const dismissedKey = `onboarding-dismissed-${listing.id}`;
        const isDismissed = localStorage.getItem(dismissedKey);
        setShowOnboarding(!isDismissed);
      }
    };
    
    if (listing) {
      checkUser();
    }
  }, [listing]);

  useEffect(() => {
    const fetchRankings = async () => {
      if (!listing?.id) return;
      
      try {
        const { data } = await supabase
          .from('v_leaderboard')
          .select(`
            city_id,
            activity_id,
            all_time_votes,
            cities (name, slug),
            activities (name, slug)
          `)
          .eq('listing_id', listing.id)
          .order('all_time_votes', { ascending: false });
        
        if (data) {
          const ranksPromises = data.map(async (entry: any) => {
            const { data: rankData } = await supabase
              .from('v_leaderboard')
              .select('listing_id')
              .eq('city_id', entry.city_id)
              .eq('activity_id', entry.activity_id)
              .order('trending_score', { ascending: false });
            
            const rank = (rankData?.findIndex((r: any) => r.listing_id === listing.id) ?? -1) + 1;
            
            return {
              cityName: entry.cities.name,
              citySlug: entry.cities.slug,
              activityName: entry.activities.name,
              activitySlug: entry.activities.slug,
              votes: entry.all_time_votes,
              rank: rank > 0 ? rank : null,
            };
          });
          
          const rankings = await Promise.all(ranksPromises);
          setCityActivityRankings(rankings.filter(r => r.rank && r.rank <= 10));
        }
      } catch (error) {
        console.error('Error fetching rankings:', error);
      }
    };
    
    fetchRankings();
  }, [listing?.id]);

  useEffect(() => {
    loadListing();
  }, [slug]);

  const loadListing = async () => {
    try {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("slug", slug as string)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setNotFound(true);
        return;
      }

      setListing(data);

      // Track view - simple increment
      await supabase
        .from('listings')
        .update({ profile_views: (data.profile_views || 0) + 1 })
        .eq('id', data.id);

      // Load coupons for this listing
      const { data: couponsData } = await supabase
        .from('coupons')
        .select('*')
        .eq('listing_id', data.id)
        .eq('is_active', true)
        .gte('valid_until', new Date().toISOString())
        .order('created_at', { ascending: false });
      
      if (couponsData) setCoupons(couponsData);

      // Load events created by this listing
      const { data: eventsData } = await supabase
        .from('events')
        .select('*')
        .eq('organizer_id', data.user_id)
        .eq('status', 'approved')
        .gte('date', new Date().toISOString())
        .order('date', { ascending: true })
        .limit(10);
      
      if (eventsData) setEvents(eventsData);

    } catch (error) {
      console.error("Error loading listing:", error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <GoneNotice
        title="Este centro ya no está disponible"
        description="La ficha que buscas ha sido retirada o ha cambiado de dirección. Explora el directorio para encontrar centros verificados cerca de ti."
        backTo="/directory"
        backLabel="Ver el directorio"
      />
    );
  }

  if (loading || !listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  const galleryImages = listing.images || [];
  const listingUrl = `${SITE_URL}/listing/${listing.slug || listing.id}`;

  // Geographic differentiation: the ISO country code drives breadcrumbs,
  // schema, titles and descriptions so each country's pages are distinct.
  const listingCountryCode = normalizeCountryCode(listing.country);
  const listingCountryLabel = countryName(listingCountryCode);
  const isForeignListing = !!listingCountryCode && !isHomeCountry(listingCountryCode);

  const structuredData = [
    generateLocalBusinessSchema({
      name: listing.name,
      description: listing.description,
      address: listing.address,
      city: listing.city,
      postalCode: listing.postal_code,
      country: listingCountryCode,
      phone: listing.phone,
      url: listing.website,
      image: listing.thumbnail_url,
      rating: listing.rating,
      reviewCount: listing.review_count,
      type: listing.type, // Pass actual listing type for proper schema mapping
    }),
    generateBreadcrumbSchema([
      { name: "Inicio", url: SITE_URL },
      { name: "Directorio", url: `${SITE_URL}/directory` },
      ...(listingCountryCode
        ? [{ name: listingCountryLabel, url: `${SITE_URL}/directory?country=${listingCountryCode}` }]
        : []),
      { name: listing.type, url: `${SITE_URL}/directory?category=${listing.type}` },
      { name: listing.city, url: `${SITE_URL}/directory?city=${listing.city}` },
      { name: listing.name, url: listingUrl },
    ]),
  ];

  const generatedMetaDescription = generateListingMetaDescription({
    name: listing.name,
    type: listing.type,
    city: listing.city,
    country: listingCountryCode,
    description: listing.short_description || listing.description,
    rating: listing.rating,
    slug: listing.slug,
    seo_description: listing.seo_description,
    is_premium: listing.is_premium,
    tags: listing.tags,
    address: listing.address,
    short_description: listing.short_description,
  });
  // Prefer admin-curated DB value when present (>=50 chars to ensure quality), otherwise generated
  const curatedDescription =
    listing.seo_description && listing.seo_description.trim().length >= 50
      ? listing.seo_description.trim()
      : null;
  const metaDescription = curatedDescription
    ? (isForeignListing && !curatedDescription.includes(listingCountryLabel)
        ? `${curatedDescription.substring(0, 157 - listingCountryLabel.length).trim()} · ${listingCountryLabel}`
        : curatedDescription
      ).substring(0, 160)
    : generatedMetaDescription;

  const benefits = [
    { icon: Target, title: "Servicio Profesional", desc: "Atención de calidad garantizada" },
    { icon: TrendingUp, title: "Resultados Comprobados", desc: "Experiencia verificada" },
    { icon: Heart, title: "Compromiso Total", desc: "Tu satisfacción es nuestra prioridad" },
    { icon: Users, title: "Comunidad Activa", desc: `${listing.vote_count || 0} votos de confianza` },
  ];

  const getTypeLabel = (type: string) => {
    const normalized = type.toLowerCase();
    if (normalized === "entrenador personal") return "Entrenador Personal";
    if (normalized === "physiotherapy") return "Fisioterapeuta";
    if (normalized === "nutrition" || normalized.includes("nutri") || normalized.includes("diet")) return "Nutricionista";
    return type;
  };

  const getRegistrationLink = (type: string) => {
    const normalized = type.toLowerCase();
    // Profesionales
    if (normalized === "entrenador personal") return "/become-trainer";
    if (normalized.includes("fisioterap") || normalized === "physiotherapy") return "/become-physiotherapist";
    if (normalized.includes("nutri") || normalized.includes("diet") || normalized === "nutrition") return "/become-nutritionist";
    // Negocios/centros
    if (
      normalized.includes("gimn") ||
      normalized.includes("gym") ||
      normalized.includes("yoga") ||
      normalized.includes("pilates") ||
      normalized.includes("crossfit") ||
      normalized.includes("artes marciales") ||
      normalized.includes("martial") ||
      normalized.includes("fitness")
    ) {
      return "/submit-listing";
    }
    return "/submit-listing";
  };

  const showRegistrationButton = () => {
    const type = listing.type.toLowerCase();
    return (
      // Profesionales
      type === "entrenador personal" ||
      type.includes("fisioterap") ||
      type === "physiotherapy" ||
      type === "nutrition" ||
      type.includes("nutri") ||
      type.includes("diet") ||
      // Negocios/centros
      type.includes("gimn") ||
      type.includes("gym") ||
      type.includes("yoga") ||
      type.includes("pilates") ||
      type.includes("crossfit") ||
      type.includes("artes marciales") ||
      type.includes("martial") ||
      type.includes("fitness")
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background to-muted/20">
      <SEO
        title={
          listing.seo_title && listing.seo_title.trim().length > 0
            ? (isForeignListing && !listing.seo_title.includes(listingCountryLabel)
                ? `${listing.seo_title.trim()} (${listingCountryLabel})`
                : listing.seo_title.trim())
            : generateListingTitle({
                name: listing.name,
                city: listing.city || 'España',
                type: listing.type,
                country: listingCountryCode,
                rating: listing.rating,
                isPremium: listing.is_premium,
                tags: listing.tags,
                slug: listing.slug,
              })
        }
        description={metaDescription}
        keywords={[
          listing.name,
          listing.type,
          listing.city,
          `${listing.type} ${listing.city}`,
          `mejor ${listing.type}`,
          ...(listing.tags || []),
        ]}
        ogImage={listing.thumbnail_url || defaultListingImage}
        ogType="website"
        structuredData={structuredData}
      />
      
      <Navigation />

      <main className="flex-grow pt-[88px] lg:pt-[120px] pb-16">
        {/* Breadcrumb Navigation */}
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-3">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">
                      <Home className="h-4 w-4" />
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/directory">Directorio</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                {listingCountryCode && (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={`/directory?country=${listingCountryCode}`}>
                          {listingCountryLabel}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                )}
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/directory?category=${listing.type}`}>
                      {listing.type}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/directory?city=${listing.city}`}>
                      {listing.city}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="max-w-[150px] truncate sm:max-w-none">
                    {listing.name}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        <article className="container mx-auto px-4 max-w-7xl">
          {/* Hero Section - Premium vs Free */}
          {listing.is_premium ? (
            <PremiumHero
              listing={listing}
              displayData={displayData}
              onContactClick={() => {
                const element = document.getElementById('contact');
                element?.scrollIntoView({ behavior: 'smooth' });
                if (trackConversion) trackConversion('contact_click');
              }}
              onVoteClick={() => {
                const voteElement = document.querySelector('[data-vote-button]');
                voteElement?.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          ) : (
            <div className="relative overflow-hidden rounded-2xl mb-12 mt-8 shadow-xl">
              {/* Hero Image with Overlay */}
              <div className="relative h-[400px] md:h-[500px]">
                <BrandedImage
                  src={listing.thumbnail_url}
                  category={listing.type || 'gym'}
                  alt={`${listing.name} - ${listing.type} en ${listing.city}`}
                  className="w-full h-full"
                  aspectRatio=""
                  containerClassName="h-full"
                  priority={true}
                  logoSize="lg"
                  logoPosition="top-right"
                  width={1200}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
                
                {/* Content Overlay - Bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                  <div className="max-w-5xl">
                    {/* Program Branding Badge */}
                    {(() => {
                      const programType = getCategoryProgramType(listing.type || '');
                      const programBranding = getProgramBranding(programType);
                      const logoUrl = getProgramLogoUrl(programType);
                      if (programBranding) {
                        return (
                          <div className="flex items-center gap-3 mb-4">
                            {logoUrl && (
                              <img 
                                src={logoUrl} 
                                alt={programBranding.name} 
                                className="h-8 w-auto drop-shadow-lg"
                              />
                            )}
                            <span className="text-white/90 text-lg font-medium drop-shadow-md">
                              Parte de {programBranding.name}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-[1.1] text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">
                      {displayData.name}
                    </h1>
                    
                    <div className="flex items-center gap-4 mb-4 flex-wrap">
                      <Badge variant="secondary" className="px-5 py-2.5 text-base font-semibold bg-white text-foreground shadow-lg">
                        {listing.type}
                      </Badge>
                      {listing.city && (
                        <div className="flex items-center gap-2 text-white bg-white/20 backdrop-blur-md px-5 py-2.5 rounded-full shadow-lg">
                          <MapPin className="w-5 h-5" />
                          <span className="text-lg font-semibold">
                            {listing.city}
                            {isForeignListing ? `, ${listingCountryLabel}` : ""}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 bg-white backdrop-blur-md rounded-full px-5 py-2.5 shadow-lg">
                        <Star className="w-5 h-5 fill-primary text-primary" />
                        <span className="text-lg font-bold text-foreground">{listing.rating || 0}</span>
                        <span className="text-sm text-muted-foreground font-medium">({listing.review_count || 0})</span>
                      </div>
                    </div>

                    {/* Public Stats */}
                    <div className="mb-6 bg-white/10 backdrop-blur-md rounded-full px-5 py-2.5 w-fit">
                      <PublicStatsDisplay 
                        views={listing.views || 0} 
                        contactClicks={listing.contact_clicks || 0}
                        variant="compact"
                      />
                    </div>

                    <p className="text-xl text-white mb-8 leading-relaxed max-w-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] font-medium">
                      {displayData.short_description || listing.description?.substring(0, 200) || ''}
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button 
                        size="lg" 
                        className="bg-gradient-primary hover:opacity-90 transition-opacity text-lg px-8 py-6 shadow-lg hover-scale"
                        onClick={() => {
                          const element = document.getElementById('contact');
                          element?.scrollIntoView({ behavior: 'smooth' });
                          if (trackConversion) trackConversion('contact_click');
                        }}
                      >
                        <Phone className="w-5 h-5 mr-2" />
                        {displayData.cta_text}
                      </Button>
                      <VotingSection 
                        listingId={listing.id} 
                        initialVoteCount={listing.vote_count || 0} 
                      />
                      <UnirseATribuButton
                        deporte={listing.type}
                        ciudad={listing.city}
                        size="lg"
                        variant="outline"
                        className="text-lg px-8 py-6 hover-scale bg-background/90 backdrop-blur hover:bg-background"
                      />
                      {showRegistrationButton() && (
                        <Button 
                          asChild
                          size="lg" 
                          className="bg-gradient-primary hover:opacity-90 transition-opacity text-lg px-8 py-6 shadow-lg hover-scale"
                        >
                          <Link to={getRegistrationLink(listing.type)}>
                            Regístrate como {getTypeLabel(listing.type)}
                          </Link>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Rankings Badges */}
              {cityActivityRankings.length > 0 && (
                <Card className="border-2 border-primary/20">
                  <CardContent className="p-6">
                    <h2 className="font-semibold mb-3 flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-primary" />
                      Rankings
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {cityActivityRankings.map((ranking, idx) => (
                        <Badge 
                          key={idx}
                          variant={ranking.rank <= 3 ? "default" : "secondary"}
                          className="text-sm px-3 py-1.5 cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => navigate(`/${ranking.citySlug}/${ranking.activitySlug}`)}
                        >
                          {ranking.rank <= 3 && "🏆 "}
                          #{ranking.rank} en {ranking.cityName} · {ranking.activityName}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Premium Value Proposition or Standard Benefits */}
              {listing.is_premium ? (
                <PremiumValueProposition listing={listing} />
              ) : (
                <Card className="border-2 border-primary/20 overflow-hidden">
                  <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <Sparkles className="w-6 h-6 text-primary" />
                      ¿Por Qué Elegirnos?
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {benefits.map((benefit, index) => (
                        <div key={index} className="flex gap-4 items-start">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <benefit.icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold mb-1">{benefit.title}</h3>
                            <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Description */}
              <Card>
                <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                  <h2 className="text-2xl font-bold">Sobre Nosotros</h2>
                </div>
                <CardContent className="p-6">
                  <div data-color-mode="light" className="markdown-preview prose prose-lg max-w-none">
                    <MDEditor.Markdown 
                      source={(() => { 
                        const raw = String(listing.seo_description || listing.description || '');
                        const normalized = normalizeHeadings(raw);
                        return hasMarkdown(normalized) ? normalized : formatToMarkdown(normalized);
                      })()} 
                      style={{ 
                        background: 'transparent',
                        color: 'inherit',
                        fontFamily: 'inherit',
                        fontSize: '1.125rem',
                        lineHeight: '1.75'
                      }}
                    />
                  </div>
                  {listing.tags && listing.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t">
                      <span className="font-semibold text-sm mr-2">Etiquetas:</span>
                      {listing.tags.map((tag: string, index: number) => (
                        <Badge 
                          key={index} 
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors px-3 py-1 hover-scale"
                          onClick={() => navigate(`/directory?search=${encodeURIComponent(tag)}`)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Related Interlinks - based on listing content */}
              <RelatedInterlinks 
                text={[listing.name, listing.short_description, listing.description, listing.seo_description, listing.type, listing.city, ...(listing.tags || [])].filter(Boolean).join(' ')} 
                maxLinks={4} 
              />

              {/* Gallery */}
              {galleryImages.length > 0 && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <ImageIcon className="w-6 h-6 text-primary" />
                      Galería
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {galleryImages.map((image: string, index: number) => (
                        <div key={index} className="aspect-square rounded-xl overflow-hidden">
                          <img 
                            src={image} 
                            alt={`${listing.name}, imagen ${index + 1}`}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                            width="400"
                            height="400"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.src = defaultListingImage; }}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Map */}
              {listing.latitude && listing.longitude && (
                <Card>
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
                    <h2 className="text-2xl font-bold flex items-center gap-3">
                      <MapPin className="w-6 h-6 text-primary" />
                      Cómo Llegar
                    </h2>
                  </div>
                  <CardContent className="p-6">
                    <SimpleLocationMap 
                      latitude={listing.latitude} 
                      longitude={listing.longitude} 
                      name={listing.name}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Professional Profiles Row - Only for FREE listings */}
              {!listing.is_premium && (
                <ProfessionalProfilesRow city={listing.city} />
              )}

              {/* Related Listings - Only for FREE listings */}
              {!listing.is_premium && (
                <RelatedListingsSection 
                  currentListingId={listing.id}
                  city={listing.city}
                  type={listing.type}
                />
              )}


              {/* Comments */}
              <CommentsSection 
                listingId={listing.id}
                listingType={listing.type}
                listingName={listing.name}
              />
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Management Access - TOP PRIORITY for owners/admins */}
              <LeadsSidebar
                listingId={listing.id}
                listingType={listing.type || ''}
                isOwnListing={currentUserId === listing.user_id}
                isAdmin={isAdmin}
                isPremium={listing.is_premium || false}
                editPath={`/listing/${listing.slug}/edit`}
                listingName={listing.name}
              />

              {/* Premium Trust Signals */}
              {listing.is_premium && (
                <PremiumTrustSignals listing={listing} />
              )}
              {/* Claim & Nearby Buttons - Stand Out */}
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white shadow-lg hover-scale"
                  onClick={() => navigate(`/cerca-de-mi?city=${listing.city}&type=${listing.type}`)}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Cerca de Mi
                </Button>
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-secondary to-secondary/80 hover:from-secondary/90 hover:to-secondary/70 text-white shadow-lg hover-scale"
                  onClick={() => toast.info("Funcionalidad de reclamación próximamente")}
                >
                  <Award className="w-4 h-4 mr-2" />
                  Reclamar
                </Button>
              </div>

              {/* Metrics - Public but in sidebar */}
              <Card className="border-2 border-primary/20">
                <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-6 border-b">
                  <h2 className="text-2xl font-bold">Tu Rendimiento</h2>
                </div>
                <CardContent className="p-6">
                  <UnifiedMetrics
                    listingId={listing.id} 
                    listingUserId={listing.user_id}
                    views={listing.profile_views ?? listing.views ?? 0}
                    isPremium={listing.is_premium || false}
                  />
                </CardContent>
              </Card>

              {/* Contact Card */}
              <Card id="contact" className="sticky top-24 border-2 border-primary/20 shadow-xl">
                <div className="bg-gradient-primary text-white p-6">
                  <h3 className="text-2xl font-bold mb-2">Contacto</h3>
                  <p className="opacity-90">¡Estamos aquí para ayudarte!</p>
                </div>
                <CardContent className="p-6 space-y-4">
                  {listing.phone && (
                    <Button 
                      className="w-full justify-start text-left h-auto py-4 hover-scale" 
                      variant="outline"
                      asChild
                      onClick={() => trackContactClick(listing.id, 'phone')}
                    >
                      <a href={`tel:${listing.phone}`}>
                        <Phone className="w-5 h-5 mr-3 text-primary" />
                        <div>
                          <div className="text-xs text-muted-foreground">Teléfono</div>
                          <div className="font-semibold">{listing.phone}</div>
                        </div>
                      </a>
                    </Button>
                  )}
                  {listing.email && (
                    <Button 
                      className="w-full justify-start text-left h-auto py-4 hover-scale" 
                      variant="outline"
                      asChild
                      onClick={() => trackContactClick(listing.id, 'email')}
                    >
                      <a href={`mailto:${listing.email}`}>
                        <Mail className="w-5 h-5 mr-3 text-primary" />
                        <div>
                          <div className="text-xs text-muted-foreground">Email</div>
                          <div className="font-semibold">{listing.email}</div>
                        </div>
                      </a>
                    </Button>
                  )}
                  {listing.website && (
                    <Button 
                      className="w-full justify-start text-left h-auto py-4 hover-scale" 
                      variant="outline"
                      asChild
                      onClick={() => trackContactClick(listing.id, 'website')}
                    >
                      <a href={listing.website} target="_blank" rel="noopener noreferrer">
                        <Globe className="w-5 h-5 mr-3 text-primary" />
                        <div>
                          <div className="text-xs text-muted-foreground">Sitio Web</div>
                          <div className="font-semibold truncate">Visitar</div>
                        </div>
                      </a>
                    </Button>
                  )}
                  {listing.address && (
                    <div className="flex gap-3 p-4 bg-muted/50 rounded-lg">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Dirección</div>
                        <div className="text-sm">{listing.address}</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Social Share Card */}
              <Card className="border-2 border-accent/20">
                <CardContent className="p-6">
                  <SocialShare 
                    url={listingUrl}
                    title={listing.name}
                    description={listing.short_description || listing.description}
                    listingId={listing.id}
                    onShare={(platform) => trackSocialShare(listing.id, platform as any)}
                  />
                </CardContent>
              </Card>


              {/* Premium Features - Always shown for premium listings */}
              {listing.is_premium && (
                <>
                  {/* Opening Hours */}
                  <OpeningHours hours={listing.opening_hours || {}} />

                  {/* Pricing Info */}
                  <PricingInfo priceInfo={listing.price_info || {}} />

                  {/* Testimonials - Always show for premium */}
                  <Testimonials testimonials={listing.testimonials || []} category={listing.type} profileName={listing.name} />

                  {/* Coupons */}
                  {coupons.length > 0 && (
                    <ListingCoupons coupons={coupons} />
                  )}

                  {/* Events */}
                  {events.length > 0 && (
                    <ListingEvents events={events} />
                  )}

                  {/* Class Schedules */}
                  {listing.class_schedules && listing.class_schedules.length > 0 && (
                    <ClassSchedules schedules={listing.class_schedules} />
                  )}
                </>
              )}

              {/* Owner Tools */}
              {(currentUserId === listing.user_id || isAdmin) && (
                <>
                  <ValueActivator 
                    listingId={listing.id}
                    isPremium={listing.is_premium || false}
                    onDismiss={() => {}}
                  />

                  {showOnboarding && currentUserId === listing.user_id && (
                    <OnboardingGuide 
                      listingId={listing.id}
                      listing={listing}
                      onDismiss={() => {
                        localStorage.setItem(`onboarding-dismissed-${listing.id}`, 'true');
                        setShowOnboarding(false);
                      }}
                    />
                  )}

                  <ProfileCompletionBar 
                    listing={listing}
                    listingId={listing.id}
                  />
                </>
              )}
            </aside>
          </div>
        </article>

        {/* Sticky Premium CTA - Only for premium listings */}
        {listing.is_premium && (
          <StickyPremiumCTA 
            listing={listing}
            displayData={displayData}
            onContactClick={() => {
              const element = document.getElementById('contact');
              element?.scrollIntoView({ behavior: 'smooth' });
              if (trackConversion) trackConversion('sticky_cta_click');
            }}
          />
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ListingDetail;
